import type { Identifier, XYCoord } from "dnd-core";
import { useDrag, useDrop } from "react-dnd";
import { Column } from "@tanstack/react-table";
import { useRef } from "react";
import { Form, ListGroupItem } from "react-bootstrap";
import { Booking } from "../booking-page/store/slice";
import styles from "./columnItem.module.scss";
import { BsList } from "react-icons/bs";

type Props = {
  index: number;
  column: Column<Booking, unknown>;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
};

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const ColumnItem = ({ index, column, moveColumn }: Props) => {
  const ref = useRef<HTMLAnchorElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: "div",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveColumn(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [, drag] = useDrag({
    type: "div",
    item: () => {
      return { id: column.id, index };
    },
  });

  drag(drop(ref));

  return (
    <ListGroupItem
      ref={ref}
      data-handler-id={handlerId}
      className={styles.item}
    >
      <Form.Check
        type="switch"
        id={`${column.id}-switch`}
        label={(column.columnDef as any).header()}
        checked={column.getIsVisible()}
        onChange={column.getToggleVisibilityHandler()}
      />
      <BsList />
    </ListGroupItem>
  );
};

export default ColumnItem;
